import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@material-ui/core';
import { Icon } from '@iconify/react';
import businessFill from '@iconify/icons-eva/briefcase-fill';
import videoFill from '@iconify/icons-eva/video-fill';
import user_api from 'utils/api/users';
import storage from 'utils/storage';
import Page from '../../components/Page';
import MyCompany from './MyCompany';
import EmployeeTrainingMaterial from './EmployeeTrainingMaterial';


// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `company-tab-${index}`,
    'aria-controls': `company-tabpanel-${index}`,
  };
}

export default function EmployeeCompanyIndex() {
  const [currentTab, setCurrentTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [myCompany, setMyCompany] = useState(false)
  const [myTraining, setMyTraining] = useState(false)
  const [loading, setLoading] = useState(true)

  // Build available tabs array based on enabled features
  const availableTabs = [];
  if (myCompany) {
    availableTabs.push({
      key: 'company',
      label: 'My Company',
      icon: businessFill,
      component: <MyCompany />
    });
  }
  if (myTraining) {
    availableTabs.push({
      key: 'training',
      label: 'Training & Development', 
      icon: videoFill,
      component: <EmployeeTrainingMaterial />
    });
  }

  const load = useCallback(async () => {
    const user = await storage.getUser()
    const jsonUser = JSON.parse(user)
    if(jsonUser && jsonUser.company) {
      
      try {
        const response = await user_api.get_features({ store: jsonUser.company })
        const features = response.data;
        
        if (features && features.length > 0) {
          
          features.forEach(feature => {
            if (feature.features && feature.features.length > 0) {
              feature.features.forEach(data => {
                if (data.feature === "My Company") {
                  setMyCompany(true)
                }
                if (data.feature === "Training And Development") {
                  setMyTraining(true)
                }
              })
            }
          })
        }
      } catch (error) {
        console.log("Error loading features:", error)
      } finally {
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Reset currentTab if it's out of bounds when features change
  useEffect(() => {
    if (currentTab >= availableTabs.length && availableTabs.length > 0) {
      setCurrentTab(0);
    }
  }, [myCompany, myTraining, currentTab, availableTabs.length]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Show loading state while API call is processing
  if (loading) {
    return (
      <Page title="My Company & Training">
        <Container maxWidth="xl">
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '50vh',
            flexDirection: 'column'
          }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" color="text.secondary" textAlign="center">
              Loading company features...
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
              Please wait while we fetch your available features.
            </Typography>
          </Box>
        </Container>
      </Page>
    );
  }

  // Don't render anything if no features are available (only after loading is complete)
  if (!myCompany && !myTraining) {
    return (
      <Page title="My Company & Training">
        <Container maxWidth="xl">
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '50vh',
            flexDirection: 'column'
          }}>
            <Typography variant="h5" color="text.secondary" textAlign="center">
              No company features are available for your account.
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
              Please contact your administrator for access.
            </Typography>
          </Box>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="My Company & Training">
      <Container maxWidth="xl">
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          mb: 4,
          mt: 2
        }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              textAlign: 'center',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '4px',
                backgroundColor: 'primary.main',
                borderRadius: '2px'
              }
            }}
          >
            Company Portal
          </Typography>
        </Box>

        {availableTabs.length > 1 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              variant={isMobile ? 'fullWidth' : 'standard'}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '1rem',
                  minHeight: 60,
                  '&.Mui-selected': {
                    fontWeight: 600
                  }
                }
              }}
            >
              {availableTabs.map((tab, index) => (
                <Tab 
                  key={tab.key}
                  label={tab.label} 
                  icon={<Icon icon={tab.icon} width={24} height={24} />}
                  iconPosition="start"
                  {...a11yProps(index)}
                  sx={{ 
                    minWidth: isMobile ? 'auto' : 200,
                    px: isMobile ? 1 : 3
                  }}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {availableTabs.map((tab, index) => (
          <TabPanel key={tab.key} value={currentTab} index={index}>
            {tab.component}
          </TabPanel>
        ))}
      </Container>
    </Page>
  );
}
